import { useMemo } from 'react'
import type { FC } from 'react'
import { Input } from 'antd'
import { CameraOutlined } from '@ant-design/icons'
import { Skeleton } from '@/components'
import { formatTs } from '@/helpers'
import { resolveTicketFileUrl } from '@/pages/help/helpers/ticket-files'
import { useMessagesStore } from '../store'
import type { T_VisitorMessageTopicEntry } from '../types'
import {
  SC_TopicItemAvatar,
  SC_TopicItemBody,
  SC_TopicEmpty,
  SC_TopicItem,
  SC_TopicItemMeta,
  SC_TopicItemSubject,
  SC_TopicList,
  SC_TopicSearchWrapper,
  SC_UnreadDot,
} from './styled'

type T_MessagesTopicListProps = {
  topics: T_VisitorMessageTopicEntry[]
  selectedTopicId: number | null
  searchText: string
  isLoading?: boolean
  onSelectTopic: (topic: T_VisitorMessageTopicEntry) => void
  onSearchTextChange: (value: string) => void
  onSearchClear: () => void
}

export const MessagesTopicList: FC<T_MessagesTopicListProps> = ({
  topics,
  selectedTopicId,
  searchText,
  isLoading = false,
  onSelectTopic,
  onSearchTextChange,
  onSearchClear,
}) => {
  const unreadMessages = useMessagesStore((s) => s.unreadMessages)

  const unreadCountsByTopicId = useMemo(() => {
    const mapById = new Map<number, number>()
    const mapBySubject = new Map<string, number>()

    const normalizeSubject = (value: string) => value.trim().toLowerCase()

    unreadMessages.forEach((message) => {
      const topicId = Number(message.topic?.id)
      if (Number.isFinite(topicId) && topicId > 0) {
        mapById.set(topicId, (mapById.get(topicId) || 0) + 1)
        return
      }

      const subjectKey = normalizeSubject(message.topic?.subject || '')
      if (!subjectKey) return
      mapBySubject.set(subjectKey, (mapBySubject.get(subjectKey) || 0) + 1)
    })

    return {
      byId: mapById,
      bySubject: mapBySubject,
    }
  }, [ unreadMessages ])

  const showSkeleton = isLoading && !topics.length

  return (
    <>
      <SC_TopicSearchWrapper>
        <Input
          value={searchText}
          placeholder='Найти сообщение'
          onChange={(e) => onSearchTextChange(e.target.value)}
          allowClear={{
            clearIcon: '×',
          }}
          onClear={onSearchClear}
        />
      </SC_TopicSearchWrapper>

      <SC_TopicList>
        {showSkeleton ? (
          <div style={{ padding: 12 }}>
            <Skeleton count={5} />
          </div>
        ) : (
          topics.length ? (
            topics.map((topic) => {
              const subjectKey = (topic.subject || '').trim().toLowerCase()
              const unreadCount = (
                unreadCountsByTopicId.byId.get(topic.id)
                || unreadCountsByTopicId.bySubject.get(subjectKey)
                || 0
              )
              const isSelected = topic.id === selectedTopicId
              const topicPhotoUrl = resolveTicketFileUrl(topic.author.photoPath)

              return (
                <SC_TopicItem
                  type='button'
                  key={topic.id}
                  onClick={() => onSelectTopic(topic)}
                  $selected={isSelected}
                >
                  <SC_TopicItemAvatar>
                    {topicPhotoUrl ? (
                      <img
                        src={topicPhotoUrl}
                        alt=''
                      />
                    ) : (
                      <CameraOutlined />
                    )}
                  </SC_TopicItemAvatar>

                  <SC_TopicItemBody>
                    <SC_TopicItemSubject $isUnread={unreadCount > 0}>
                      {topic.subject || 'Без темы'}
                    </SC_TopicItemSubject>

                    <SC_TopicItemMeta>
                      <span>{formatTs(topic.lastMessageTime.getTime())}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {unreadCount > 0 && <SC_UnreadDot />}
                        {unreadCount > 0 && <span>{unreadCount}</span>}
                      </div>
                    </SC_TopicItemMeta>
                  </SC_TopicItemBody>
                </SC_TopicItem>
              )
            })
          ) : (
            <SC_TopicEmpty>Сообщений не найдено</SC_TopicEmpty>
          )
        )}
      </SC_TopicList>
    </>
  )
}
